import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { AcceptShareResponse, DocumentShareInvitation } from '../../types/DocumentShare';

interface AcceptDocumentShareDialogProps {
  open: boolean;
  onClose: () => void;
  invitation: DocumentShareInvitation | null;
  existingDocumentNames: string[];
  onAccepted: (created: AcceptShareResponse) => void;
}

const AcceptDocumentShareDialog: React.FC<AcceptDocumentShareDialogProps> = ({
  open,
  onClose,
  invitation,
  existingDocumentNames,
  onAccepted,
}) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !invitation) return;
    const baseName = `${invitation.sourceDocumentName} (from ${invitation.sourceUsername})`;
    let candidate = baseName;
    let counter = 1;
    while (existingDocumentNames.includes(candidate)) {
      candidate = `${baseName} (${counter})`;
      counter += 1;
    }
    setNewName(candidate);
    setError(null);
    setSubmitting(false);
  }, [open, invitation, existingDocumentNames]);

  const handleNameChange = (value: string) => {
    setNewName(value);
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Document name cannot be empty');
    } else if (existingDocumentNames.includes(trimmed)) {
      setError('A document with this name already exists');
    } else {
      setError(null);
    }
  };

  const handleAccept = async () => {
    if (!invitation || error || !newName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post<AcceptShareResponse>(
        `/documents/shares/${invitation.id}/accept`,
        { newName: newName.trim() },
      );
      onAccepted(response.data);
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const msg =
        (typeof data === 'string' ? data : (data as { message?: string; detail?: string })?.message) ??
        (typeof data === 'object' && data !== null ? (data as { detail?: string }).detail : undefined) ??
        'Failed to accept share';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/documents/shares/${invitation.id}/decline`);
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const msg =
        (typeof data === 'string' ? data : (data as { message?: string; detail?: string })?.message) ??
        (typeof data === 'object' && data !== null ? (data as { detail?: string }).detail : undefined) ??
        'Failed to decline share';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!invitation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Accept shared document</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>{invitation.sourceUsername}</strong> shared &quot;{invitation.sourceDocumentName}&quot; with you.
          A copy will be added to your library.
        </Typography>
        {invitation.message && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            &quot;{invitation.message}&quot;
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Name for your copy"
          value={newName}
          onChange={(e) => handleNameChange(e.target.value)}
          fullWidth
          disabled={submitting}
          sx={{ mt: 2 }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDecline} disabled={submitting} color="inherit">
          Decline
        </Button>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleAccept} variant="contained" disabled={submitting || !!error}>
          {submitting ? <CircularProgress size={22} /> : 'Accept & open'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcceptDocumentShareDialog;
